import {
  CartErrorEvent,
  CartLinesUpdateEvent,
  CartViewEvent,
  CollectionUpdateEvent,
  PageViewEvent,
  ProductSelectEvent,
  SearchUpdateEvent,
  createViewEventElement,
} from '@theme/standard-events';
import { CartRefreshEvent, FCEvents, getCartEventData } from '@fc/events';
import { resolveCartStateEndpoint } from '@fc/cart';

const STANDARD_ACTION_SOURCE = 'fitcheck-standard-action';
const STANDARD_EVENT_SOURCE = 'fitcheck-standard-event';
const STANDARD_CART_VIEW_PAYLOAD_SELECTOR = '[data-standard-cart-view-payload]';
const PRODUCT_SELECT_TIMEOUT_MS = 8000;
const STANDARD_ACTION_RETRY_INTERVAL_MS = 250;
const STANDARD_ACTION_RETRY_LIMIT = 20;
const pendingCartLineUpdates = new Map();
const pendingProductSelects = new WeakMap();
let pendingAjaxCartRead = null;
let actionsConfigured = false;
let actionsConfigureRetryTimer = null;
let actionsConfigureRetryCount = 0;

function onReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
    return;
  }

  callback();
}

function createDeferred(EventClass) {
  if (typeof EventClass?.createPromise === 'function') {
    return EventClass.createPromise();
  }

  let resolve;
  let reject;
  const promise = new Promise((resolvePromise, rejectPromise) => {
    resolve = resolvePromise;
    reject = rejectPromise;
  });

  return { promise, resolve, reject };
}

function cssEscape(value) {
  if (window.CSS?.escape) return window.CSS.escape(String(value));
  return String(value).replace(/["\\]/g, '\\$&');
}

function parseJSON(value) {
  if (typeof value !== 'string' || value.trim() === '') return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function toPositiveInteger(value, fallback = 1) {
  const number = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function toNullableInteger(value) {
  const number = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(number) ? number : null;
}

function currentTemplateName() {
  return window.FitCheck?.template?.name || document.querySelector('main[data-template]')?.dataset.template || '';
}

function handleFromURL(value) {
  if (typeof value !== 'string' || value.trim() === '') return null;

  try {
    const url = new URL(value, window.location.origin);
    const segments = url.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] || null;
  } catch {
    const segments = value.split('?')[0].split('/').filter(Boolean);
    return segments[segments.length - 1] || null;
  }
}

function moneyFromVariantPrice(value) {
  if (value == null || value === '') return null;

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;

  const amount = Number.isInteger(numeric) && numeric >= 100 ? numeric / 100 : numeric;
  const currencyCode = window.FitCheck?.money?.currency_code || window.Shopify?.currency?.active || null;
  if (!currencyCode) return null;

  return {
    amount: amount.toFixed(2),
    currencyCode,
  };
}

function compactObject(value) {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry != null && entry !== ''));
}

function registerViewEventElement() {
  if (customElements.get('s-view-event')) return;
  customElements.define('s-view-event', createViewEventElement());
}

function dispatchPageView() {
  document.dispatchEvent(
    new PageViewEvent({
      page: {
        template: currentTemplateName(),
        title: document.title,
        url: window.location.href,
      },
    }),
  );
}

function dispatchCartView(element) {
  if (!(element instanceof Element)) return;

  const payload = parseJSON(element.getAttribute('data-standard-cart-view-payload'));
  if (!payload) return;

  element.dispatchEvent(new CartViewEvent(payload));
}

function dispatchCartPageViews() {
  for (const element of document.querySelectorAll('cart-items[data-standard-cart-view-context="page"]')) {
    if (!(element instanceof HTMLElement)) continue;
    if (element.dataset.standardCartViewDispatched === 'true') continue;
    element.dataset.standardCartViewDispatched = 'true';
    dispatchCartView(element);
  }
}

function bindCartDrawerViews() {
  document.addEventListener('dialog:open', (event) => {
    const dialog = event.detail?.dialog;
    if (!(dialog instanceof HTMLElement)) return;

    const cartItems = dialog.querySelector('cart-items[data-standard-cart-view-context="dialog"]');
    dispatchCartView(cartItems);
  });
}

function normalizeVariant(resource) {
  if (!resource || typeof resource !== 'object') return null;

  return compactObject({
    id: resource.id,
    title: resource.title,
    availableForSale: resource.availableForSale ?? resource.available_for_sale ?? resource.available,
    price: moneyFromVariantPrice(resource.price),
  });
}

function productPayloadFromElement(element, fallback = {}) {
  const root = element instanceof Element ? element.closest('product-form, variant-select, swatch-picker, .shopify-section') : null;
  const productUrl = fallback.productUrl || root?.dataset?.productUrl || window.location.pathname;
  const title =
    root?.querySelector?.('[data-product-title], .product-title, h1, h2, h3')?.textContent?.trim() ||
    document.querySelector('h1')?.textContent?.trim() ||
    null;

  return compactObject({
    id: fallback.productId || root?.dataset?.productId,
    title,
    handle: handleFromURL(productUrl),
  });
}

function selectedOptionsFromPick(data) {
  if (!data || typeof data !== 'object') return [];

  return Array.isArray(data.selectedOptions)
    ? data.selectedOptions
    : Array.isArray(data.options)
      ? data.options
      : [];
}

function handleVariantPick(event) {
  const target = event.target instanceof Element ? event.target : document;
  const data = event.detail?.resource || {};
  if (data.standardEventDispatched === true) return;

  const deferred = createDeferred(ProductSelectEvent);
  const timer = setTimeout(() => {
    deferred.resolve({ variant: data.variantId ? { id: data.variantId } : null });
    pendingProductSelects.delete(target);
  }, PRODUCT_SELECT_TIMEOUT_MS);

  pendingProductSelects.set(target, { deferred, timer });

  target.dispatchEvent(
    new ProductSelectEvent({
      selectedOptions: selectedOptionsFromPick(data),
      product: productPayloadFromElement(target, data),
      promise: deferred.promise,
    }),
  );
}

function handleVariantChange(event) {
  const target = event.target instanceof Element ? event.target : null;
  const pending = target ? pendingProductSelects.get(target) : null;
  if (!pending) return;

  clearTimeout(pending.timer);
  pending.deferred.resolve({ variant: normalizeVariant(event.detail?.resource) });
  pendingProductSelects.delete(target);
}

async function readAjaxCart() {
  if (pendingAjaxCartRead) return pendingAjaxCartRead;

  pendingAjaxCartRead = (async () => {
    const response = await fetch(resolveCartStateEndpoint(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Cart state request failed (${response.status})`);
    }

    return response.json();
  })().finally(() => {
    pendingAjaxCartRead = null;
  });

  return pendingAjaxCartRead;
}

function cartFromAjax(ajaxCart) {
  if (typeof CartLinesUpdateEvent.createCartFromAjaxResponse === 'function') {
    return CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);
  }

  return ajaxCart;
}

function isCartSummary(value) {
  if (!isPlainObject(value)) return false;

  const totalQuantity = toNullableInteger(value.totalQuantity);
  return (
    totalQuantity !== null &&
    isPlainObject(value.cost) &&
    Array.isArray(value.lines) &&
    Array.isArray(value.discountCodes)
  );
}

function cartSummaryFromPayload(payload) {
  const cart = isPlainObject(payload) ? payload.cart : null;
  return isCartSummary(cart) ? cart : null;
}

function cartSummaryFromSectionMarkup(markup) {
  if (typeof markup !== 'string' || markup.trim() === '') return null;

  const doc = new DOMParser().parseFromString(markup, 'text/html');
  const payloadElement = doc.querySelector(STANDARD_CART_VIEW_PAYLOAD_SELECTOR);
  const payload = parseJSON(payloadElement?.getAttribute('data-standard-cart-view-payload'));
  return cartSummaryFromPayload(payload);
}

function cartSummaryFromSections(sections) {
  if (!isPlainObject(sections)) return null;

  for (const markup of Object.values(sections)) {
    const cart = cartSummaryFromSectionMarkup(markup);
    if (cart) return cart;
  }

  return null;
}

function cartLineOperationId(event) {
  const data = getCartEventData(event);
  return data.cartAddOperationId || event.detail?.sourceId || null;
}

function handleCartAddPending(event) {
  const data = getCartEventData(event);
  const operationId = cartLineOperationId(event);
  if (!operationId || pendingCartLineUpdates.has(operationId)) return;
  if (data.action !== 'add' || !data.variantId) return;

  const target = event.target instanceof Element ? event.target : document;
  const deferred = createDeferred(CartLinesUpdateEvent);
  const context = target.closest?.('dialog') ? 'dialog' : 'product';

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines: [
        {
          merchandiseId: data.variantId,
          quantity: toPositiveInteger(data.quantityRequested, 1),
        },
      ],
      promise: deferred.promise,
    }),
  );

  pendingCartLineUpdates.set(operationId, { deferred, target });
}

async function resolvePendingCartLineUpdate(event) {
  const operationId = cartLineOperationId(event);
  const pending = operationId ? pendingCartLineUpdates.get(operationId) : null;
  if (!pending) return;

  pendingCartLineUpdates.delete(operationId);

  try {
    const data = getCartEventData(event);
    let ajaxCart = null;
    const cart = cartSummaryFromSections(data.sections) ?? cartFromAjax((ajaxCart = await readAjaxCart()));
    const eventItemCount = toNullableInteger(event.detail?.data?.itemCount);
    const ajaxItemCount = ajaxCart ? toNullableInteger(ajaxCart.item_count) : null;
    const cartItemCount = toNullableInteger(cart?.totalQuantity);
    pending.deferred.resolve({
      cart,
      detail: compactObject({
        didError: false,
        itemCount: eventItemCount ?? ajaxItemCount ?? cartItemCount,
        items: Array.isArray(ajaxCart?.items) ? ajaxCart.items : null,
        productId: data.productId,
        sections: data.sections,
        source: STANDARD_EVENT_SOURCE,
        sourceId: event.detail?.sourceId || null,
      }),
    });
  } catch (error) {
    pending.deferred.reject(error);
  }
}

function rejectPendingCartLineUpdate(event) {
  const operationId = cartLineOperationId(event);
  const pending = operationId ? pendingCartLineUpdates.get(operationId) : null;
  if (!pending) return;

  pendingCartLineUpdates.delete(operationId);

  const data = getCartEventData(event);
  const message = data.description || data.message || 'Cart update failed';
  const error = new Error(message);

  pending.target.dispatchEvent(
    new CartErrorEvent({
      error: message,
      code: data.status === 'error' ? 'INVALID' : 'SERVICE_UNAVAILABLE',
    }),
  );
  pending.deferred.reject(error);
}

function bindInternalEventBridge() {
  document.addEventListener(FCEvents.variantPick, handleVariantPick);
  document.addEventListener(FCEvents.variantChange, handleVariantChange);
  document.addEventListener(FCEvents.cartAddPending, handleCartAddPending);
  document.addEventListener(FCEvents.cartRefresh, (event) => {
    const data = getCartEventData(event);
    if (data.action === 'add' && data.didError === false) {
      void resolvePendingCartLineUpdate(event);
    }
  });
  document.addEventListener(FCEvents.cartFail, rejectPendingCartLineUpdate);
  document.addEventListener(FCEvents.cartAddPendingClear, (event) => {
    const data = getCartEventData(event);
    if (data.didError || data.aborted) {
      rejectPendingCartLineUpdate(event);
    }
  });
}

function standardEventTarget(meta = {}) {
  if (meta.type === 'shopify:cart:note-update') {
    return document.querySelector('cart-note') || document.querySelector('cart-items') || document;
  }

  if (meta.type === 'shopify:cart:discount-update') {
    return document.querySelector('cart-discount') || document.querySelector('cart-items') || document;
  }

  if (meta.type === 'shopify:cart:lines-update' && meta.action === 'add') {
    return document.querySelector('product-form') || document.querySelector('cart-items') || document;
  }

  return document.querySelector('cart-items') || document;
}

function dispatchInternalCartRefresh(result) {
  const itemCount = toNullableInteger(result?.cart?.totalQuantity);
  if (itemCount === null) return;

  document.dispatchEvent(
    new CartRefreshEvent(result?.cart || {}, STANDARD_ACTION_SOURCE, {
      itemCount,
      source: STANDARD_ACTION_SOURCE,
      sections: null,
    }),
  );
}

function openThemeCart() {
  const drawer = document.querySelector('cart-drawer');
  if (typeof drawer?.open === 'function') {
    drawer.open();
    return;
  }

  if (typeof drawer?.showDialog === 'function') {
    drawer.showDialog({ reason: 'standard-action' });
    return;
  }

  window.location.href = window.FitCheck?.routes?.cart_url || '/cart';
}

function configureStandardActionsNow() {
  if (actionsConfigured) return true;

  const actions = window.Shopify?.actions;
  if (!actions) return false;

  const canConfigureUpdateCart = typeof actions.updateCart?.configure === 'function';
  const canConfigureOpenCart = typeof actions.openCart?.configure === 'function';

  if (!canConfigureUpdateCart || !canConfigureOpenCart) return false;

  actions.updateCart.configure({
    eventTarget: standardEventTarget,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      dispatchInternalCartRefresh(result);
      return {
        ...result,
        detail: {
          ...(result?.detail || {}),
          source: STANDARD_ACTION_SOURCE,
        },
      };
    },
  });

  actions.openCart.configure({
    handler() {
      openThemeCart();
    },
  });

  actionsConfigured = true;
  return true;
}

function scheduleStandardActionsConfiguration() {
  if (actionsConfigured || actionsConfigureRetryTimer !== null) return;

  actionsConfigureRetryCount = 0;
  actionsConfigureRetryTimer = window.setInterval(() => {
    actionsConfigureRetryCount += 1;

    if (configureStandardActionsNow() || actionsConfigureRetryCount >= STANDARD_ACTION_RETRY_LIMIT) {
      window.clearInterval(actionsConfigureRetryTimer);
      actionsConfigureRetryTimer = null;
    }
  }, STANDARD_ACTION_RETRY_INTERVAL_MS);
}

export function configureStandardActions() {
  if (configureStandardActionsNow()) return true;

  scheduleStandardActionsConfiguration();
  return false;
}

function resultRootFromTarget(target) {
  const root = target instanceof Element ? target.closest('[data-standard-results-surface]') : null;
  return root instanceof HTMLElement ? root : null;
}

function currentResultRoot(root) {
  const id = root?.dataset?.fitResultsShellId;
  if (!id) return root;

  const current = document.querySelector(`[data-fit-results-shell-id="${cssEscape(id)}"]`);
  return current instanceof HTMLElement ? current : root;
}

function readResultsCount(root) {
  const current = currentResultRoot(root);
  return toNullableInteger(current?.dataset?.standardProductsCount) ?? 0;
}

function parsedProductFilters(EventClass, params) {
  if (typeof EventClass?.parseProductFilters !== 'function') return undefined;
  return EventClass.parseProductFilters(params);
}

export function dispatchStandardResultsUpdate(target, params) {
  const root = resultRootFromTarget(target);
  if (!root) return null;

  const surface = root.dataset.standardResultsSurface;
  const deferred =
    surface === 'search' ? createDeferred(SearchUpdateEvent) : createDeferred(CollectionUpdateEvent);
  const sortKey = params.get('sort_by') || undefined;

  if (surface === 'collection') {
    target.dispatchEvent(
      new CollectionUpdateEvent({
        collection: compactObject({
          id: root.dataset.standardCollectionId,
          handle: root.dataset.standardCollectionHandle,
          productsCount: readResultsCount(root),
        }),
        productFilters: parsedProductFilters(CollectionUpdateEvent, params),
        sortKey,
        promise: deferred.promise,
      }),
    );

    return {
      settle(renderPromise) {
        Promise.resolve(renderPromise).then(
          () => deferred.resolve({ productsCount: readResultsCount(root) }),
          (error) => deferred.reject(error),
        );
      },
    };
  }

  if (surface === 'search') {
    target.dispatchEvent(
      new SearchUpdateEvent({
        search: compactObject({
          query: params.get('q') || root.dataset.standardSearchQuery || '',
          productFilters: parsedProductFilters(SearchUpdateEvent, params),
          sortKey,
        }),
        promise: deferred.promise,
      }),
    );

    return {
      settle(renderPromise) {
        Promise.resolve(renderPromise).then(
          () => deferred.resolve({ totalCount: readResultsCount(root) }),
          (error) => deferred.reject(error),
        );
      },
    };
  }

  return null;
}

function exposeFitCheckHooks() {
  const fitCheck = window.FitCheck || {};
  fitCheck.configureStandardActions = configureStandardActions;
  fitCheck.standardStorefront = {
    configureStandardActions,
    dispatchStandardResultsUpdate,
  };
  window.FitCheck = fitCheck;
}

registerViewEventElement();
exposeFitCheckHooks();
bindInternalEventBridge();
bindCartDrawerViews();

onReady(() => {
  dispatchPageView();
  dispatchCartPageViews();
  configureStandardActions();
});
